import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useConfig } from '../context/ConfigContext';
import { useClients } from '../context/ClientsContext';
import { useUsers } from '../context/UsersContext';
import { useEmployer } from '../context/EmployerContext';
import { useToast } from './Toast';
import Icon from './Icon';
import AnalysisControls from './AnalysisControls';
import EmployerOverridesPanel from './EmployerOverridesPanel';
import SaveEmployerDialog from './SaveEmployerDialog';
import EligibilityAdvisor from './EligibilityAdvisor';
import { C, S, planColors } from '../utils/styles';
import { fmt, fmtK, fmtPct, fmtFiling } from '../utils/formatters';
import { optimizeEmployee, optimizeEmployeeWithEligibility, parseCSV } from '../utils/calculations';
import { calculateEligibilityStats, hasEligibilityColumns, getEligibilityStatusLabel } from '../utils/eligibility';
import { SAMPLE_CSV } from '../data/seedData';
import { generatePDF } from '../utils/pdfGenerator';

import { CSV_TEMPLATE, CSV_TEMPLATE_FILENAME } from '../data/csvTemplate';
import IBOSView from './ibos/IBOSView';
import SmartCensusUpload from './employer/SmartCensusUpload';
import JurisdictionImpactsPanel from './JurisdictionImpactsPanel';
import { buildClientSummary } from '../utils/buildClientSummary';

export default function BulkView() {
  const { config } = useConfig();
  const { activeClient, lockAnalysisConfig, addDocument, loadClient, updateCRM, updateCurrentClient, syncClientSummary } = useClients();
  const { currentUser } = useUsers();
  const { currentEmployer, updateCurrentEmployer, hasUnsavedChanges } = useEmployer();
  const addToast = useToast();
  const [employees, setEmployees] = useState([]);
  const [csvText, setCsvText] = useState("");
  const [showSmartUpload, setShowSmartUpload] = useState(false);
  // Track the previously-selected employer/client id so we can detect a
  // transition from "had a selection" -> "no selection" and clear the local
  // workforce state. Without this, clicking "Clear Selection" in the client
  // selector leaves stale data on screen because the sync-from-context effect
  // below only POPULATES local state -- it never resets it.
  const lastSelectionRef = React.useRef({ employerId: null, clientId: null });
  const [sortField, setSortField] = useState("benefit");
  const [sortDir, setSortDir] = useState("desc");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [pdfShowHcRevenue, setPdfShowHcRevenue] = useState(config.reportContent?.show_hc_revenue_default || false);
  const [pdfLockSettings, setPdfLockSettings] = useState(true); // Default to true for safety
  const [pdfIncludeIbos, setPdfIncludeIbos] = useState(false);
  const [pdfIncludeOptions, setPdfIncludeOptions] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  // Workforce sub-view: 'section125' (default) or 'ibos'
  const [workforceView, setWorkforceView] = useState('section125');
  
  // Create temporary config with analysis control overrides from activeClient
  // Merge client analysis config with system config
  const analysisConfig = useMemo(() => {
    const overrides = activeClient?.analysisConfig?.overrides || currentEmployer?.overrides || {};
    const controls = activeClient?.analysisConfig?.controls || {};
    
    // Get values from activeClient.analysisConfig or fall back to system defaults
    const payFrequency = controls.payFrequency || config?.engineSettings?.payPeriodsPerYear || 26;
    const optimizationStrategy = controls.optimizationStrategy || config?.engineSettings?.optimizationStrategy || 'employee_benefit';
    const minEmployeeNetBenefit = controls.minEmployeeNet || config?.engineSettings?.minEmployeeNetBenefit || 0;
    const includeFuta = controls.includeFuta !== undefined ? controls.includeFuta : (config?.engineSettings?.includeFutaSavings || false);
    
    return {
      ...config,
      engineSettings: {
        ...config.engineSettings,
        payPeriodsPerYear: payFrequency,
        optimizationStrategy,
        minEmployeeNetBenefit,
        includeFutaSavings: includeFuta,
      },
      employerPlanRules: {
        ...config.employerPlanRules,
        ...overrides,
      },
      // Filter plan tiers based on overrides and exclude archived plans
      // Handle both old format ('1500') and new format ('Plan 1500')
      planTiers: (() => {
        // First filter out archived plans
        const activePlans = (config.planTiers || []).filter(tier => !tier.archived);
        
        if (!overrides.availablePlanTiers || overrides.availablePlanTiers.length === 0) {
          return activePlans;
        }
        const filtered = activePlans.filter(tier => 
          overrides.availablePlanTiers.includes(tier.label) || 
          overrides.availablePlanTiers.includes(tier.id)
        );
        // If filter resulted in empty array, fall back to all active tiers
        return filtered.length > 0 ? filtered : activePlans;
      })(),
    };
  }, [config, activeClient, currentEmployer]);
  
  // Helper: detect if employees array uses parsed schema (has .salary)
  // vs raw schema (e.g., from naive ClientDetailDrawer upload — has "annual salary" key)
  const hasParsedSchema = (arr) => Array.isArray(arr) && arr.length > 0 && (typeof arr[0]?.salary === 'number' || arr[0]?.salary !== undefined);
  
  // Sync employees from BOTH activeClient and currentEmployer
  // IMPORTANT: This only runs when client/employer IDs change (not on every save round-trip),
  // so that local state isn't stomped after Save Employer / Download PDF refreshes activeClient.
  useEffect(() => {
    const prevEmployerId = lastSelectionRef.current.employerId;
    const prevClientId = lastSelectionRef.current.clientId;
    const nextEmployerId = currentEmployer?.id || null;
    const nextClientId = activeClient?.id || null;

    // Detect a transition from "had a selection" -> "no selection". This
    // covers the Clear Selection button in EmployerSelector + any other path
    // that nulls out the active client/employer. Clearing local state here
    // ensures the workforce results panel doesn't keep showing the previous
    // client's numbers under the now-empty header.
    const hadSelection = !!(prevEmployerId || prevClientId);
    const hasSelection = !!(nextEmployerId || nextClientId);
    if (hadSelection && !hasSelection) {
      setEmployees([]);
      setCsvText('');
      setShowSmartUpload(false);
      lastSelectionRef.current = { employerId: null, clientId: null };
      return;
    }
    lastSelectionRef.current = { employerId: nextEmployerId, clientId: nextClientId };

    // Priority 1: Load from currentEmployer (if set via EmployerSelector or local upload)
    if (currentEmployer?.employees && currentEmployer.employees.length > 0) {
      if (hasParsedSchema(currentEmployer.employees)) {
        setEmployees(currentEmployer.employees);
        setCsvText(currentEmployer.originalCsvText || '');
      } else if (currentEmployer.originalCsvText) {
        // Re-parse raw schema using parseCSV so calculations work
        const parsed = parseCSV(currentEmployer.originalCsvText, config.stateData);
        setEmployees(parsed);
        setCsvText(currentEmployer.originalCsvText);
      }
      return; // Don't check activeClient if we have currentEmployer data
    }

    // Priority 2: Load from activeClient (if uploaded via client drawer)
    if (activeClient?.employees && activeClient.employees.length > 0) {
      if (hasParsedSchema(activeClient.employees)) {
        setEmployees(activeClient.employees);
        setCsvText(activeClient.originalCsvText || '');
      } else if (activeClient.originalCsvText) {
        // Raw upload from ClientDetailDrawer — re-parse for proper analysis
        const parsed = parseCSV(activeClient.originalCsvText, config.stateData);
        setEmployees(parsed);
        setCsvText(activeClient.originalCsvText);
      } else {
        // Fall back to whatever shape we have
        setEmployees(activeClient.employees);
        setCsvText('');
      }
    }
  }, [currentEmployer?.id, activeClient?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update employer when employees change
  useEffect(() => {
    if (currentEmployer && employees.length > 0) {
      updateCurrentEmployer({
        employees,
        originalCsvText: csvText,
      });
    }
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Mirror local employees + csvText into currentClient so that
  // "Save Employer" and "Download PDF" persist the latest census to backend
  // and so the post-save activeClient refresh doesn't blank the workforce view.
  // (Summary mirror lives below — after `analyzed` is computed.)

  const handleFile = useCallback((e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => { const t = ev.target.result; setCsvText(t); setEmployees(parseCSV(t, config.stateData)); };
    r.readAsText(f);
  }, [config.stateData]);

  const loadSample = useCallback(() => {
    setCsvText(SAMPLE_CSV);
    setEmployees(parseCSV(SAMPLE_CSV, config.stateData));
  }, [config.stateData]);

  const analyzed = useMemo(() => employees.map((emp) => {
    return optimizeEmployeeWithEligibility(emp, analysisConfig);
  }), [employees, analysisConfig]);

  // Mirror employees + summary into currentClient (must come AFTER `analyzed`).
  // This ensures `client.summary.eligible / annualEmployerFica / ...` stays in
  // sync with the live workforce computation. Without this, the Clients
  // dropdown, the Clients table, and the Update-Client modal show stale
  // `0/X eligible` after a Smart Census Upload (because Smart Upload posts
  // employees to the backend but doesn't recompute the rollup).
  useEffect(() => {
    if (activeClient && employees.length > 0) {
      const currentEmployees = activeClient.employees || [];
      const currentCsv = activeClient.originalCsvText || '';
      const newSummary = buildClientSummary(analyzed);
      const summaryDifferent = JSON.stringify(activeClient.summary || {}) !== JSON.stringify(newSummary);
      if (currentEmployees !== employees || currentCsv !== csvText || summaryDifferent) {
        updateCurrentClient({ employees, originalCsvText: csvText, summary: newSummary });
      }
    }
  }, [employees, csvText, analyzed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-PATCH stale summary to backend (one-shot per client load).
  // Why: if a client's persisted `summary` is empty/stale (e.g. employees
  // were uploaded via the old path that never wrote summary, or via Smart
  // Upload before this fix), the Clients dropdown / Clients table / Command
  // Center dashboard all show 0/0. We auto-correct as soon as the user
  // navigates to that client's Workforce view, instead of forcing them to
  // click Save Employer to repair stale records.
  const autoSyncedClientIds = useRef(new Set());
  useEffect(() => {
    if (!activeClient?.id) return;
    if (employees.length === 0) return;
    if (autoSyncedClientIds.current.has(activeClient.id)) return;
    const computed = buildClientSummary(analyzed);
    const persisted = activeClient.summary || {};
    const computedEligible = computed.eligible || 0;
    const persistedEligible = persisted.eligible || 0;
    const stale =
      (persisted.totalEmployees || 0) !== (computed.totalEmployees || 0) ||
      (persistedEligible === 0 && computedEligible > 0) ||
      (persisted.annualEmployerFica || 0) === 0 && (computed.annualEmployerFica || 0) > 0 ||
      Math.abs((persisted.annualEmployerFica || 0) - (computed.annualEmployerFica || 0)) > 1;
    if (!stale) {
      autoSyncedClientIds.current.add(activeClient.id);
      return;
    }
    // Mark first to prevent re-fire while the PATCH is in flight
    autoSyncedClientIds.current.add(activeClient.id);
    (async () => {
      const result = await syncClientSummary(activeClient.id, computed);
      if (!result) {
        // Allow retry on next mount (e.g. transient network)
        autoSyncedClientIds.current.delete(activeClient.id);
      }
    })();
  }, [activeClient?.id, analyzed, employees.length]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Calculate eligibility statistics
  const eligibilityStats = useMemo(() => calculateEligibilityStats(analyzed), [analyzed]);
  
  // Check if CSV has eligibility columns
  const eligibilityColumnCheck = useMemo(() => hasEligibilityColumns(employees), [employees]);

  const sorted = useMemo(() => {
    const a = [...analyzed];
    a.sort((x, y) => {
      let va, vb;
      if (sortField === "name") return sortDir === "asc" ? x.name.localeCompare(y.name) : y.name.localeCompare(x.name);
      if (sortField === "salary") { 
        va = x.salary; 
        vb = y.salary; 
      }
      else if (sortField === "benefit") { 
        va = x.optimal?.employeeAnnualBenefit || 0; 
        vb = y.optimal?.employeeAnnualBenefit || 0; 
      }
      else if (sortField === "employer") { 
        va = x.optimal?.employerAnnualFica || 0; 
        vb = y.optimal?.employerAnnualFica || 0; 
      }
      else if (sortField === "hc") { 
        va = x.optimal?.hcAnnualRevenue || 0; 
        vb = y.optimal?.hcAnnualRevenue || 0; 
      }
      else { 
        va = x.optimal?.monthlyPremium || 0; 
        vb = y.optimal?.monthlyPremium || 0; 
      }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return a;
  }, [analyzed, sortField, sortDir]);

  const totals = useMemo(() => {
    if (!analyzed.length) return null;
    const t = { 
      count: analyzed.length, 
      eligible: 0, 
      waiting: 0,
      ineligible: 0,
      notRecommended: 0, 
      empBenefit: 0, 
      emprFica: 0, 
      hcRevenue: 0, 
      byPlan: {},
      // Eligibility breakdown
      ineligible_1099: 0,
      ineligible_hours: 0,
      ineligible_owner: 0,
      ineligible_union: 0,
      ineligible_salary: 0,
    };
    analyzed.forEach((e) => {
      // Check eligibility status
      if (e.eligibility) {
        if (e.eligibility.eligible && e.hasViable) {
          t.eligible++;
          t.empBenefit += e.optimal.employeeAnnualBenefit;
          t.emprFica += e.optimal.employerAnnualFica;
          t.hcRevenue += e.optimal.hcAnnualRevenue;
          t.byPlan[e.optimal.label] = (t.byPlan[e.optimal.label] || 0) + 1;
        } else if (e.eligibility.status === 'waiting') {
          t.waiting++;
        } else if (e.eligibility.status === '1099') {
          t.ineligible++;
          t.ineligible_1099++;
        } else if (e.eligibility.status === 'hours') {
          t.ineligible++;
          t.ineligible_hours++;
        } else if (e.eligibility.status === 'owner') {
          t.ineligible++;
          t.ineligible_owner++;
        } else if (e.eligibility.status === 'union') {
          t.ineligible++;
          t.ineligible_union++;
        } else if (e.eligibility.status === 'salary') {
          // Phase 17 (UX correction): "salary too low" means residual would
          // drop below the applicable minimum-wage floor — that's a hard FLSA
          // compliance gate, not an advisor preference. Bucketed as Ineligible.
          t.ineligible++;
          t.ineligible_salary++;
        }
      } else {
        // Fallback for legacy data without eligibility
        if (e.hasViable) {
          t.eligible++;
          t.empBenefit += e.optimal.employeeAnnualBenefit;
          t.emprFica += e.optimal.employerAnnualFica;
          t.hcRevenue += e.optimal.hcAnnualRevenue;
          t.byPlan[e.optimal.label] = (t.byPlan[e.optimal.label] || 0) + 1;
        } else {
          // Legacy fallback: bucketed as Ineligible (same treatment as salary status)
          t.ineligible++;
          t.ineligible_salary++;
        }
      }
    });
    return t;
  }, [analyzed]);
  
  // Update employer summary when totals change
  useEffect(() => {
    if (currentEmployer && totals) {
      updateCurrentEmployer({
        summary: {
          totalEmployees: totals.count,
          eligible: totals.eligible,
          ineligible: totals.ineligible,
          waiting: totals.waiting,
          notRecommended: totals.notRecommended,
          annualEmployerFica: totals.emprFica,
          annualEmployeeBenefit: totals.empBenefit,
          annualHcRevenue: totals.hcRevenue,
        },
      });
    }
  }, [totals]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (f) => { if (sortField === f) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortField(f); setSortDir("desc"); } };
  const SH = ({ field, children, align }) => (
    <th onClick={() => handleSort(field)} style={{ ...S.th, textAlign: align || "left", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {children} {sortField === field ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const exportCSV = useCallback(() => {
    if (!analyzed.length) return;
    const h = "Name,ID,Department,Salary,State,Filing,Eligible,Optimal Plan,Premium/Mo,Claim/Mo,Spread/Mo,Tax Savings/Mo,Employee Net/Mo,Employee Net/Yr,Employer FICA/Yr,HC Revenue/Yr,Eligibility Status\n";
    const rows = analyzed.map((e) => {
      const o = e.optimal;
      const eligibilityStatus = e.eligibility ? (e.eligibility.eligible ? 'Eligible' : e.eligibility.reason || 'Not Eligible') : 'Unknown';
      
      if (e.hasViable && o) {
        return [e.name, e.employeeId || '', e.department || '', e.salary, e.state, e.filing, "Yes", o.label, o.monthlyPremium, o.monthlyClaim, o.spread, o.calc.diff.totalTax.toFixed(2), o.employeePerPeriodBenefit.toFixed(2), o.employeeAnnualBenefit.toFixed(2), o.employerAnnualFica.toFixed(2), o.hcAnnualRevenue.toFixed(2), eligibilityStatus].join(",");
      }
      return [e.name, e.employeeId || '', e.department || '', e.salary, e.state, e.filing, "No", "None", "", "", "", "", "", "", "", "", eligibilityStatus].join(",");
    }).join("\n");
    const blob = new Blob([h + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "healthcues-workforce-analysis.csv"; a.click();
    URL.revokeObjectURL(url);
  }, [analyzed]);

  const downloadTemplate = useCallback(() => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = CSV_TEMPLATE_FILENAME;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleGeneratePDF = useCallback(async () => {
    if (!activeClient) {
      addToast('No client selected', 'error');
      return;
    }
    
    const companyName = activeClient.company?.legalName || activeClient.company?.dba || 'Unnamed Client';
    const preparedBy = `${currentUser.firstName} ${currentUser.lastName}, HealthCues`;
    
    setPdfGenerating(true);
    // Small delay to ensure UI updates (loading state shows)
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      // Compute IBOS snapshot if requested
      let ibosSnapshot = null;
      if (pdfIncludeIbos) {
        try {
          const { computeIBOS } = await import('../utils/ibosCalculations');
          const avgSalary = analyzed.length > 0
            ? Math.round(analyzed.reduce((s, e) => s + (Number(e.salary) || 0), 0) / analyzed.length)
            : 0;
          ibosSnapshot = computeIBOS({
            workforceTotals: totals || {},
            avgAnnualSalary: avgSalary,
            ibosLayers: config?.ibosLayers || [],
            clientIbosConfig: activeClient?.ibosConfig || {},
          });
        } catch (err) {
          console.warn('IBOS compute failed, skipping section:', err);
        }
      }

      await generatePDF(analyzed, config, {
        companyName,
        preparedBy,
        showHcRevenue: pdfShowHcRevenue,
        includeIbos: pdfIncludeIbos,
        includeSavedOptions: pdfIncludeOptions,
        savedOptions: pdfIncludeOptions ? activeClient?.savedOptions : null,
        ibosSnapshot,
      });
      
      // Create document metadata with config snapshot
      const document = {
        id: `doc-${Date.now()}`,
        type: 'pdf_proposal',
        name: `Section 125 Analysis - ${companyName}`,
        generatedAt: new Date().toISOString(),
        generatedBy: currentUser?.id,
        preparedBy,
        // Snapshot of analysis config at time of generation
        analysisConfigSnapshot: {
          overrides: { ...(activeClient.analysisConfig?.overrides || {}) },
          controls: { ...(activeClient.analysisConfig?.controls || {}) },
          snapshotAt: new Date().toISOString(),
        },
        metadata: {
          employeeCount: analyzed.length,
          showHcRevenue: pdfShowHcRevenue,
          includeIbos: pdfIncludeIbos,
        }
      };
      
      // Add document to client
      await addDocument(activeClient.id, document);
      
      // Lock the analysis settings if checkbox was checked and not already locked
      if (pdfLockSettings && !activeClient.analysisConfig?.isLocked) {
        await lockAnalysisConfig(
          activeClient.id, 
          currentUser?.id, 
          `PDF proposal generated for ${companyName}`
        );
        addToast('✓ Analysis settings locked to preserve proposal configuration', 'success');
      }
      
      setPdfGenerating(false);
      setShowPdfModal(false);
      addToast('PDF report generated — check your downloads!');
    } catch (err) {
      console.error('PDF generation error:', err);
      setPdfGenerating(false);
      addToast('PDF generation failed: ' + (err?.message || 'Unknown error'), 'error');
    }
  }, [analyzed, config, pdfShowHcRevenue, pdfLockSettings, pdfIncludeIbos, totals, activeClient, currentUser, lockAnalysisConfig, addDocument, addToast]);

  return (
    <div data-testid="bulk-view" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Analysis Controls Bar */}
      <AnalysisControls />
      
      {/* Employer Overrides Panel */}
      <EmployerOverridesPanel />
      
      <div style={S.card}>
        <h3 style={S.heading}>Workforce Census Upload</h3>
        <p style={{ fontSize: 12, color: C.textLight, margin: "0 0 12px", lineHeight: 1.5 }}>
          Upload your employee census in any supported format — our standard template, a HealthCues partner template, or a payroll-system export (ADP, Paycom, Paycor, Gusto). Smart Upload auto-detects the format and reconciles against actual employer taxes when available.
        </p>

        {/* Expected format preview */}
        <div style={{ marginBottom: 14, padding: "12px 16px", background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`, overflowX: "auto" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Expected CSV Format</div>
          <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
            <thead>
              <tr>
                {["Name", "Employee ID", "Email", "Dept", "Annual Salary", "State", "Filing", "Hire Date", "Weekly Hrs", "Worker Type", "Owner %", "HCE", "Union"].map((h) => (
                  <th key={h} style={{ padding: "4px 8px", textAlign: "left", fontWeight: 700, color: C.accent, borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap", ...S.mono, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["John Smith", "EMP001", "john@example.com", "Ops", "$31,200", "CA", "Single", "03/15/2024", "40", "W-2", "0", "No", "No"],
                ["Jane Doe", "EMP002", "jane@example.com", "Sales", "$45,000", "TX", "MFJ", "11/01/2025", "40", "W-2", "0", "No", "No"],
                ["Bob Johnson", "EMP003", "bob@example.com", "Kitchen", "$22,000", "NY", "Single", "01/10/2026", "25", "W-2", "0", "No", "No"],
              ].map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} style={{ padding: "3px 8px", color: cell ? C.textMid : C.borderLight, borderBottom: `1px solid ${C.borderLight}`, ...S.mono, fontSize: 10 }}>{cell || '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 10, color: C.textLight, lineHeight: 1.5 }}>
            <strong>Required:</strong> Name, Annual Salary, State, Filing Status &nbsp;·&nbsp; <strong>Optional:</strong> Email, Employee ID, Department, Hire Date, Weekly Hours, Worker Type, Ownership %, HCE Flag, Union Member, Pay Frequency, Employment Type, Annual Hours
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <button data-testid="download-template-btn" onClick={downloadTemplate} style={{ padding: "8px 16px", background: C.card, borderRadius: 8, border: `2px solid ${C.accent}`, color: C.accent, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Download Template
          </button>
          <button
            data-testid="smart-upload-trigger"
            onClick={() => setShowSmartUpload((v) => !v)}
            style={{
              padding: "8px 16px",
              background: showSmartUpload ? C.bg : C.accent,
              borderRadius: 8,
              border: showSmartUpload ? `1px solid ${C.border}` : 'none',
              color: showSmartUpload ? C.textMid : "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name={showSmartUpload ? "close-outline" : "cloud-upload-outline"} style={{ fontSize: 14 }} />
            {showSmartUpload ? 'Cancel upload' : 'Upload Census'}
          </button>
          <label style={{ padding: "8px 16px", background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, color: C.textMid, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Quick CSV upload
            <input data-testid="csv-upload-input" type="file" accept=".csv,.txt" onChange={handleFile} style={{ display: "none" }} />
          </label>
          <button data-testid="load-sample-btn" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            loadSample();
          }} style={{ padding: "8px 16px", background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, color: C.textMid, fontSize: 12, fontWeight: 600, cursor: "pointer", WebkitTapHighlightColor: 'rgba(0,0,0,0)', touchAction: 'manipulation' }}>
            Load 30 Employee Sample
          </button>
          {analyzed.length > 0 && (
            <>
              <button 
                onClick={() => setShowSaveDialog(true)} 
                style={{ 
                  padding: "8px 16px", 
                  background: hasUnsavedChanges ? C.orangeLight : C.blueLight, 
                  borderRadius: 8, 
                  border: `1px solid ${hasUnsavedChanges ? C.orange : C.blue}`, 
                  color: hasUnsavedChanges ? C.orange : C.blue, 
                  fontSize: 12, 
                  fontWeight: 700, 
                  cursor: "pointer", 
                  marginLeft: "auto",
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon name="save-outline" style={{ fontSize: 14 }} />
                {currentEmployer?.id ? 'Update' : 'Save'} Employer
              </button>
              <button data-testid="export-csv-btn" onClick={exportCSV} style={{ padding: "8px 16px", background: C.greenLight, borderRadius: 8, border: `1px solid ${C.greenBorder}`, color: C.green, fontSize: 12, fontWeight: 700, cursor: "pointer", ...S.mono }}>
                Export Results CSV
              </button>
              <button 
                data-testid="download-pdf-btn" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (activeClient) {
                    setShowPdfModal(true);
                  }
                }} 
                disabled={!activeClient}
                style={{ 
                  padding: "8px 16px", 
                  background: activeClient ? "#1a2332" : C.bg, 
                  borderRadius: 8, 
                  border: activeClient ? "none" : `1px solid ${C.border}`, 
                  color: activeClient ? "#fff" : C.textLight, 
                  fontSize: 12, 
                  fontWeight: 700, 
                  cursor: activeClient ? "pointer" : "not-allowed", 
                  opacity: activeClient ? 1 : 0.5,
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)', 
                  touchAction: 'manipulation' 
                }}
                title={!activeClient ? "Select a client first to generate PDF" : "Download PDF Report"}
              >
                Download PDF Report
              </button>
            </>
          )}
        </div>
        {showSmartUpload && (
          <div
            data-testid="bulk-smart-upload-panel"
            style={{
              marginTop: 12,
              marginBottom: 12,
              padding: 16,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              background: C.bg,
            }}
          >
            <SmartCensusUpload
              client={activeClient || null}
              persist={false}
              onUploaded={({ employees: parsedEmployees, meta }) => {
                setEmployees(parsedEmployees);
                setCsvText('');
                setShowSmartUpload(false);
                addToast(
                  `✓ Census uploaded — ${parsedEmployees.length} employees parsed${meta?.sourceFormat ? ` (${meta.sourceFormat})` : ''}.`,
                  'success'
                );
              }}
            />
          </div>
        )}
        <textarea data-testid="csv-paste-area" value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="Or paste CSV data here..." style={{ ...S.input, height: 70, resize: "vertical", fontSize: 11 }} />
        {csvText && !employees.length && <button data-testid="analyze-btn" onClick={() => setEmployees(parseCSV(csvText, config.stateData))} style={{ marginTop: 6, padding: "6px 14px", background: C.accent, color: "#fff", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Analyze</button>}
      </div>

      {totals && (
        <>
          {/* Workforce View Toggle: Section 125 vs IBOS */}
          <div
            data-testid="workforce-view-toggle"
            style={{
              display: 'inline-flex',
              gap: 4,
              padding: 4,
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              alignSelf: 'flex-start',
            }}
          >
            {[
              { id: 'section125', label: 'Section 125 Analysis', icon: 'calculator-outline' },
              { id: 'ibos', label: 'IBOS — Full Optimization', icon: 'layers-outline' },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`workforce-view-tab-${t.id}`}
                onClick={() => setWorkforceView(t.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  background: workforceView === t.id ? C.accent : 'transparent',
                  color: workforceView === t.id ? '#fff' : C.textMid,
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <Icon name={t.icon} style={{ fontSize: 14 }} />
                {t.label}
              </button>
            ))}
          </div>

          {workforceView === 'ibos' ? (
            <IBOSView
              totals={totals}
              analyzed={analyzed}
              companyName={activeClient?.company?.legalName || activeClient?.company?.dba || 'Current Analysis'}
            />
          ) : (
          <>
          {/* Missing Eligibility Columns Banner */}
          {employees.length > 0 && !eligibilityColumnCheck.hasAny && (
            <div style={{ 
              ...S.card, 
              background: C.orangeLight, 
              border: `1px solid ${C.orange}`, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              padding: '12px 16px'
            }}>
              <Icon name="information-circle" style={{ fontSize: 20, color: C.orange }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                  Limited eligibility data provided
                </div>
                <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.5 }}>
                  Hire date, weekly hours, and worker type columns not found. Screening based on financial viability only. Download the full template for complete eligibility analysis.
                </div>
              </div>
              <button 
                onClick={() => document.querySelector('[data-testid="download-template-btn"]')?.click()}
                style={{ 
                  padding: '6px 12px', 
                  background: C.orange, 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 6, 
                  fontSize: 11, 
                  fontWeight: 700, 
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Get Template
              </button>
            </div>
          )}
          
          {/* Main Dashboard Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
            {[
              { label: "Total Employees", value: totals.count, bg: C.card, color: C.text },
              { label: "Eligible", value: totals.eligible, sub: fmtPct(totals.eligible / totals.count) + " participation", bg: C.greenLight, color: C.green },
              { label: "Waiting Period", value: totals.waiting, bg: totals.waiting > 0 ? C.orangeLight : C.card, color: totals.waiting > 0 ? C.orange : C.textLight },
              { label: "Ineligible", value: totals.ineligible, sub: totals.ineligible_salary > 0 ? `${totals.ineligible_salary} salary too low` : null, bg: totals.ineligible > 0 ? C.redLight : C.card, color: totals.ineligible > 0 ? C.red : C.textLight },
              { label: "Total Employee Benefit/Yr", value: fmtK(totals.empBenefit), bg: C.greenLight, color: C.green },
            ].map((c, i) => (
              <div key={i} style={{ ...S.card, textAlign: "center", padding: "14px 10px", background: c.bg, border: "none" }}>
                <div style={{ fontSize: 9, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: c.color, ...S.mono }}>{c.value}</div>
                {c.sub && <div style={{ fontSize: 9, color: C.textLight, marginTop: 2 }}>{c.sub}</div>}
              </div>
            ))}
          </div>

          {/* Secondary Metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            <div style={{ ...S.card, textAlign: "center", padding: "14px 10px", background: C.blueLight, border: "none" }}>
              <div style={{ fontSize: 9, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Total Employer FICA/Yr</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.blue, ...S.mono }}>{fmtK(totals.emprFica)}</div>
              <div style={{ fontSize: 9, color: C.textLight, marginTop: 2 }}>zero cost to employer</div>
            </div>
            <div style={{ ...S.card, textAlign: "center", padding: "14px 10px", background: C.purpleLight, border: "none" }}>
              <div style={{ fontSize: 9, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>HC Spread Revenue/Yr</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.purple, ...S.mono }}>{fmtK(totals.hcRevenue)}</div>
            </div>
          </div>
          
          {/* Ineligibility Breakdown Card */}
          {(totals.ineligible > 0 || totals.waiting > 0) && (
            <div style={S.card}>
              <h3 style={S.heading}>Eligibility Breakdown</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {totals.ineligible_1099 > 0 && (
                  <div style={{ padding: '10px', background: C.redLight, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 2 }}>1099 Contractors</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{totals.ineligible_1099}</div>
                  </div>
                )}
                {totals.ineligible_hours > 0 && (
                  <div style={{ padding: '10px', background: C.redLight, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 2 }}>Below Hours Min</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{totals.ineligible_hours}</div>
                  </div>
                )}
                {totals.ineligible_owner > 0 && (
                  <div style={{ padding: '10px', background: C.redLight, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 2 }}>&gt;2% Shareholder</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{totals.ineligible_owner}</div>
                  </div>
                )}
                {totals.ineligible_union > 0 && (
                  <div style={{ padding: '10px', background: C.redLight, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 2 }}>Union Excluded</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{totals.ineligible_union}</div>
                  </div>
                )}
                {totals.ineligible_salary > 0 && (
                  <div style={{ padding: '10px', background: C.redLight, borderRadius: 8 }} data-testid="elig-breakdown-salary">
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 2 }}>Salary Too Low (min-wage floor)</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{totals.ineligible_salary}</div>
                  </div>
                )}
                {totals.waiting > 0 && (
                  <div style={{ padding: '10px', background: C.orangeLight, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 2 }}>In Waiting Period</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.orange }}>{totals.waiting}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {Object.keys(totals.byPlan).length > 0 && (
            <div style={S.card}>
              <h3 style={S.heading}>Optimal Plan Distribution</h3>
              <div style={{ display: "flex", gap: 4, height: 36, borderRadius: 8, overflow: "hidden" }}>
                {(config.planTiers || []).filter(t => !t.archived).map((t) => {
                  const count = totals.byPlan[t.label] || 0;
                  if (!count) return null;
                  return (
                    <div key={t.id} style={{ flex: count / totals.eligible, background: planColors[t.label] || C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#fff", minWidth: 50 }}>
                      {t.label.replace("Plan ", "")} ({count})
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ ...S.card, overflow: "auto" }}>
            <h3 style={S.heading}>Employee Optimization Results</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <SH field="name">Name</SH>
                  <SH field="salary" align="right">Salary</SH>
                  <th style={S.th}>State</th>
                  <th style={{ ...S.th, textAlign: "center" }} title="Filing Status">Filing</th>
                  <SH field="plan" align="center">Plan</SH>
                  <th style={{ ...S.th, textAlign: "right" }}>Tax Save/Mo</th>
                  <SH field="benefit" align="right">Emp. Net/Mo</SH>
                  <SH field="employer" align="right">Empr FICA/Yr</SH>
                  <SH field="hc" align="right">HC Spread/Yr</SH>
                  <th style={{ ...S.th, textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((e, i) => {
                  const statusLabel = e.eligibility ? getEligibilityStatusLabel(e.eligibility, e.hasViable ? e.optimal : null) : null;
                  const isEligible = e.eligibility?.eligible && e.hasViable;
                  const isWaiting = e.eligibility?.status === 'waiting';
                  const isIneligible = e.eligibility && !e.eligibility.eligible && e.eligibility.status !== 'salary' && e.eligibility.status !== 'waiting';
                  
                  return (
                    <tr key={i} style={{ background: isIneligible ? C.redLight : !e.hasViable ? C.orangeLight : i % 2 === 0 ? "transparent" : C.bg }}>
                      <td style={{ ...S.td, fontWeight: 600, color: C.text }}>
                        {e.name}
                        {e.eligibility?.isHCE && (
                          <span style={{ 
                            marginLeft: 6, 
                            background: C.blueLight, 
                            color: C.blue, 
                            padding: '2px 6px', 
                            borderRadius: 4, 
                            fontSize: 9, 
                            fontWeight: 700 
                          }}>HCE</span>
                        )}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11 }}>{fmt(e.salary)}</td>
                      <td style={{ ...S.td, ...S.mono, fontSize: 11 }}>{e.state}</td>
                      <td style={{ ...S.td, textAlign: "center", ...S.mono, fontSize: 10, fontWeight: 700, color: C.textMid }} title={e.filing}>{fmtFiling(e.filing)}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        {isEligible
                          ? <span style={{ background: C.accentLight, color: C.accent, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, ...S.mono }}>{e.optimal.label}</span>
                          : <span style={{ background: C.redLight, color: C.red, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>None</span>
                        }
                      </td>
                      <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: isEligible ? C.green : C.textLight }}>{isEligible ? fmt(e.optimal.calc.diff.totalTax) : "—"}</td>
                      <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, fontWeight: 700, color: isEligible ? C.green : C.textLight }}>{isEligible ? "+" + fmt(e.optimal.employeePerPeriodBenefit) : "—"}</td>
                      <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: isEligible ? C.blue : C.textLight, fontWeight: 600 }}>{isEligible ? fmt(e.optimal.employerAnnualFica) : "—"}</td>
                      <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: isEligible ? C.purple : C.textLight, fontWeight: 600 }}>{isEligible ? fmt(e.optimal.hcAnnualRevenue) : "—"}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        {statusLabel ? (
                          <span style={{ 
                            color: statusLabel.color === 'green' ? C.green : statusLabel.color === 'orange' ? C.orange : C.red, 
                            fontWeight: 600, 
                            fontSize: 10, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 4, 
                            justifyContent: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <Icon name={statusLabel.icon} style={{ fontSize: '14px' }} />
                            <span dangerouslySetInnerHTML={{ __html: statusLabel.label.replace('✓ ', '').replace('⏳ ', '').replace('✗ ', '') }} />
                          </span>
                        ) : (
                          isEligible ? (
                            <span style={{ color: C.green, fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                              <Icon name="checkmark-circle" style={{ fontSize: '14px' }} />
                              Eligible
                            </span>
                          ) : (
                            <span style={{ color: C.red, fontWeight: 600, fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                              <Icon name="close-circle" style={{ fontSize: '14px' }} />
                              Need {fmt(e.breakEvenSalary)}
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Phase 17 — Jurisdiction Impacts (renders only if any local-tax savings or non-federal min-wage exclusions exist) */}
          {analyzed.length > 0 && (
            <JurisdictionImpactsPanel analyzed={analyzed} />
          )}

          {/* Eligibility Maximization Advisor */}
          {analyzed.length > 0 && (
            <EligibilityAdvisor 
              employees={employees}
              config={analysisConfig}
              analyzed={analyzed}
            />
          )}
          </>
          )}
        </>
      )}

      {/* PDF Generation Modal */}
      {showPdfModal && activeClient && (
        <div data-testid="pdf-modal" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => !pdfGenerating && setShowPdfModal(false)}>
          <div style={{ ...S.card, width: 440, padding: '28px 32px', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: C.text, margin: '0 0 4px' }}>Generate PDF Report</h3>
            <p style={{ fontSize: 12, color: C.textLight, margin: '0 0 18px', lineHeight: 1.5 }}>
              This will create a professional employer-facing analysis document.
            </p>

            {/* Client Info Display */}
            <div style={{ 
              marginBottom: 20, 
              padding: 12, 
              background: C.bg, 
              borderRadius: 8, 
              border: `1px solid ${C.border}` 
            }}>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 4, fontWeight: 600, letterSpacing: '0.05em' }}>
                CLIENT
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                {activeClient.company?.legalName || activeClient.company?.dba || 'Unnamed Client'}
              </div>
              <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
                Prepared by: {currentUser.firstName} {currentUser.lastName}
              </div>
            </div>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                data-testid="pdf-hc-revenue-toggle"
                onClick={() => setPdfShowHcRevenue(!pdfShowHcRevenue)}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  background: pdfShowHcRevenue ? C.purple : '#ccc',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: pdfShowHcRevenue ? 21 : 3,
                  transition: 'left 0.2s',
                }} />
              </button>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Include HC Revenue column</div>
                <div style={{ fontSize: 10, color: C.textLight }}>Typically hidden for employer-facing documents</div>
              </div>
            </div>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                data-testid="pdf-lock-settings-toggle"
                onClick={() => setPdfLockSettings(!pdfLockSettings)}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  background: pdfLockSettings ? C.accent : '#ccc',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: pdfLockSettings ? 21 : 3,
                  transition: 'left 0.2s',
                }} />
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="lock-closed" style={{ fontSize: 14, color: pdfLockSettings ? C.accent : C.textLight }} />
                  Lock analysis settings
                </div>
                <div style={{ fontSize: 10, color: C.textLight, lineHeight: 1.4 }}>
                  Prevent changes to overrides & controls used in this proposal
                </div>
              </div>
            </div>

            {/* IBOS Inclusion Toggle */}
            <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                data-testid="pdf-include-ibos-toggle"
                onClick={() => setPdfIncludeIbos(!pdfIncludeIbos)}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  background: pdfIncludeIbos ? C.accent : '#ccc',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: pdfIncludeIbos ? 21 : 3,
                  transition: 'left 0.2s',
                }} />
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="layers-outline" style={{ fontSize: 14, color: pdfIncludeIbos ? C.accent : C.textLight }} />
                  Include IBOS — Integrated Benefits Optimization Strategy
                </div>
                <div style={{ fontSize: 10, color: C.textLight, lineHeight: 1.4 }}>
                  Adds multi-layer optimization summary, layer details, and roadmap pages
                </div>
              </div>
            </div>

            {/* Saved Options Inclusion Toggle */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, opacity: activeClient?.savedOptions ? 1 : 0.5 }}>
              <button
                data-testid="pdf-include-options-toggle"
                disabled={!activeClient?.savedOptions}
                onClick={() => setPdfIncludeOptions(!pdfIncludeOptions)}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  background: pdfIncludeOptions ? C.accent : '#ccc',
                  position: 'relative', cursor: activeClient?.savedOptions ? 'pointer' : 'not-allowed', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: pdfIncludeOptions ? 21 : 3, transition: 'left 0.2s' }} />
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="bookmark-outline" style={{ fontSize: 14, color: pdfIncludeOptions ? C.accent : C.textLight }} />
                  Include benefit options comparison
                </div>
                <div style={{ fontSize: 10, color: C.textLight, lineHeight: 1.4 }}>
                  {activeClient?.savedOptions
                    ? 'Adds the side-by-side option comparison page (rendered from the employer\u2019s saved options)'
                    : 'No saved options on this client — run the Section 105 advisor first'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPdfModal(false)}
                disabled={pdfGenerating}
                style={{ padding: '8px 18px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, color: C.textMid, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >Cancel</button>
              <button
                data-testid="pdf-generate-btn"
                onClick={handleGeneratePDF}
                disabled={pdfGenerating}
                style={{
                  padding: '8px 22px', 
                  background: pdfGenerating ? '#ccc' : '#1a2332', 
                  borderRadius: 8, 
                  border: 'none',
                  color: '#fff', 
                  fontSize: 12, 
                  fontWeight: 700, 
                  cursor: pdfGenerating ? 'not-allowed' : 'pointer',
                  opacity: pdfGenerating ? 0.7 : 1,
                }}
              >
                {pdfGenerating ? 'Generating...' : 'Generate & Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Employer Dialog */}
      <SaveEmployerDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={async (savedClient) => {
          // Load the saved client as the active client
          if (savedClient && savedClient.id) {
            loadClient(savedClient.id);
            const clientName = savedClient.company?.legalName || savedClient.company?.dba || 'Client';
            
            // Auto-promote Lead → Prospect when employer is saved for analysis
            if (savedClient.crm?.stage === 'lead') {
              await updateCRM({ stage: 'prospect' });
              addToast(`✓ ${clientName} saved and moved to Prospect stage`, 'success');
            } else {
              addToast(`✓ ${clientName} saved successfully`, 'success');
            }
          }
          setShowSaveDialog(false);
        }}
      />
    </div>
  );
}
